/**
 * 欢迎项目工具函数
 * 处理欢迎项目的创建和管理
 */

import { User } from '@supabase/supabase-js';
import { ProjectService } from '../services/projectService';
import { TaskService } from '../services/taskService';
import { generateWelcomeProjectData } from '../data/welcomeProjectData';

// 全局创建锁，防止并发创建
let isCreatingWelcomeProject = false;

/**
 * 检查用户是否是新用户（注册时间在24小时内）
 * 主要用于应用引导功能
 */
export const isNewUserForOnboarding = (user: User): boolean => {
  if (!user || !user.created_at) return false;

  const createdAt = new Date(user.created_at);
  const now = new Date();
  const diffHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

  console.log('[WelcomeProjectUtils] Checking if user is new for onboarding:', {
    userId: user.id,
    userCreatedAt: user.created_at,
    diffHours,
    isNewUser: diffHours <= 24
  });

  // 如果用户是在 24 小时内创建的，认为是新用户
  return diffHours <= 24;
};

/**
 * 检查是否已经创建过欢迎项目
 */
export const hasWelcomeProject = (): boolean => {
  try {
    // 如果正在创建中，也认为已经有了欢迎项目
    if (isCreatingWelcomeProject) {
      return true;
    }
    const status = localStorage.getItem('tempo-welcome-project-created');
    // 检查是否已创建完成或正在创建中
    return status === 'true' || status === 'creating';
  } catch (e) {
    console.warn('[WelcomeProjectUtils] Failed to check welcome project status:', e);
    return false;
  }
};

/**
 * 标记欢迎项目已创建
 */
export const markWelcomeProjectCreated = (projectId: string): void => {
  try {
    localStorage.setItem('tempo-welcome-project-created', 'true');
    localStorage.setItem('tempo-welcome-project-id', projectId);
  } catch (e) {
    console.warn('[WelcomeProjectUtils] Failed to mark welcome project created:', e);
  }
};

/**
 * 获取欢迎项目ID
 */
export const getWelcomeProjectId = (): string | null => {
  try {
    return localStorage.getItem('tempo-welcome-project-id');
  } catch (e) {
    console.warn('[WelcomeProjectUtils] Failed to get welcome project ID:', e);
    return null;
  }
};

/**
 * 创建欢迎项目
 */
export const createWelcomeProject = async (): Promise<string> => {
  // 检查是否已经在创建中
  if (isCreatingWelcomeProject) {
    throw new Error('Welcome project is already being created');
  }

  try {
    // 设置创建锁
    isCreatingWelcomeProject = true;
    console.log('[WelcomeProjectUtils] Creating welcome project...');

    // 立即标记为正在创建，防止重复创建
    localStorage.setItem('tempo-welcome-project-created', 'creating');

    // 生成带时间的欢迎项目数据
    const projectData = generateWelcomeProjectData();

    // 创建项目
    const project = await ProjectService.createProject({
      title: projectData.title,
      context: projectData.context
    });

    console.log('[WelcomeProjectUtils] Welcome project created:', project.id);

    // 创建任务
    for (const taskData of projectData.tasks) {
      await TaskService.createTask({
        ...taskData,
        projectId: project.id
      });
    }

    console.log('[WelcomeProjectUtils] Welcome tasks created');

    // 标记已创建
    markWelcomeProjectCreated(project.id);

    return project.id;
  } catch (error) {
    console.error('[WelcomeProjectUtils] Failed to create welcome project:', error);
    // 创建失败时清除标记
    try {
      localStorage.removeItem('tempo-welcome-project-created');
      localStorage.removeItem('tempo-welcome-project-id');
    } catch (e) {
      console.warn('[WelcomeProjectUtils] Failed to clear welcome project status on error:', e);
    }
    throw error;
  } finally {
    // 无论成功失败都要释放创建锁
    isCreatingWelcomeProject = false;
  }
};

/**
 * 检查是否启用了欢迎项目功能
 */
export const isWelcomeProjectEnabled = (): boolean => {
  // 检查环境变量，默认为 false
  const enabled = import.meta.env.VITE_ENABLE_WELCOME_PROJECT === 'true';

  console.log('[WelcomeProjectUtils] Welcome project feature enabled:', enabled);

  return enabled;
};

/**
 * 检查是否需要创建欢迎项目
 */
export const shouldCreateWelcomeProject = (user: User): boolean => {
  // 首先检查功能是否启用
  if (!isWelcomeProjectEnabled()) {
    console.log('[WelcomeProjectUtils] Welcome project feature is disabled');
    return false;
  }

  // 只要用户存在且没有欢迎项目，就创建
  // 不再基于注册时间判断，因为老用户也可能需要欢迎项目
  if (!user) return false;

  const hasProject = hasWelcomeProject();
  const shouldCreate = !hasProject;

  console.log('[WelcomeProjectUtils] shouldCreateWelcomeProject check:', {
    userId: user.id,
    userEmail: user.email,
    hasProject,
    shouldCreate,
    featureEnabled: true
  });

  return shouldCreate;
};
